import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { useIsMobile } from "@/hooks/useMobile";
import { Search, Check, X, Users, Clock, UserCheck } from "lucide-react";
import { useState, CSSProperties } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export default function GymMembers() {
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const isMobile = useIsMobile();
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  const utils = trpc.useUtils();

  const { data: pendingMembers, isLoading: pendingLoading } = trpc.gym.members.pending.useQuery();
  const { data: allMembers, isLoading: membersLoading } = trpc.gym.members.list.useQuery({ search: searchQuery || undefined });

  const approveMutation = trpc.gym.members.approve.useMutation({
    onSuccess: () => {
      toast.success("Mitglied genehmigt");
      utils.gym.members.pending.invalidate();
      utils.gym.members.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const rejectMutation = trpc.gym.members.reject.useMutation({
    onSuccess: () => {
      toast.success("Anfrage abgelehnt");
      utils.gym.members.pending.invalidate();
      utils.gym.members.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const gymMembers = allMembers?.filter(m => m.roles?.includes("gym_member"));

  // Auth check
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laden...</div>;
  }

  if (!user || !hasAnyRole(user.roles, ["admin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold tracking-tight text-center">
            Anmeldung erforderlich
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Der Zugriff auf diesen Bereich erfordert Administratorrechte.
          </p>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        {isMobile && (
          <header className="flex h-14 items-center gap-2 border-b px-4">
            <SidebarTrigger />
          </header>
        )}
        <div className="p-6 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Gym Mitglieder</h1>
            <p className="text-muted-foreground">
              Verwalten Sie Mitgliedsanfragen und bestehende Mitglieder
            </p>
          </div>

          {/* Stats */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ausstehende Anfragen</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingMembers?.length ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Aktive Mitglieder</CardTitle>
                <UserCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{gymMembers?.length ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Gesamt Benutzer</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{allMembers?.length ?? 0}</div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="pending" className="relative">
                Anfragen
                {(pendingMembers?.length ?? 0) > 0 && (
                  <Badge className="ml-2 bg-orange-500">{pendingMembers?.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="members">Mitglieder</TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-4">
              {pendingLoading ? (
                <div className="text-center py-8 text-muted-foreground">Laden...</div>
              ) : pendingMembers?.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Keine ausstehenden Anfragen</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {pendingMembers?.map(member => (
                    <Card key={member.id}>
                      <CardContent className="py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{member.name || "Unbekannt"}</span>
                              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                Ausstehend
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{member.email}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Registriert: {format(new Date(member.createdAt), "d. MMM yyyy, HH:mm", { locale: de })}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => approveMutation.mutate({ userId: member.id })}
                              disabled={approveMutation.isPending}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Genehmigen
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              onClick={() => rejectMutation.mutate({ userId: member.id })}
                              disabled={rejectMutation.isPending}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Ablehnen
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="members" className="mt-4 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach Name oder E-Mail..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {membersLoading ? (
                <div className="text-center py-8 text-muted-foreground">Laden...</div>
              ) : gymMembers?.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Users className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Keine Mitglieder gefunden</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {gymMembers?.map(member => (
                    <Card key={member.id}>
                      <CardContent className="py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{member.name || "Unbekannt"}</span>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Aktiv
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{member.email}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Mitglied seit: {format(new Date(member.createdAt), "d. MMM yyyy", { locale: de })}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
